/**
 * Copyright 2023-2024 Highway9 Networks Inc.
 */
import { renderToStaticMarkup } from "react-dom/server";
import moment from "moment";
import { MetricGraph } from "~/types/metricGraph";
import { JSEncrypt } from "jsencrypt";
import { EventSummary } from "~/types/event";
import { PresetsType } from '@h9/ui-lib';
import type { JSX } from "react";

export function dec2hex(dec: number) {
  return "0x" + Number(dec).toString(16).toUpperCase();
}

export function hex2dec(hex: string) {
  return parseInt(hex, 16);
}

export const TimeFormat = "MMM DD, YYYY h:mm a z";
export const TimestampFormat = "MMM DD, YYYY HH:mm:ss a";
export const ShortTimeFormat = "MMM DD, YYYY h:mm a";
export const DateTimeFormat = "ddd, MMM DD, YYYY hh:mm:ss.SSS a z";
export const GraphAxisDatetimeFormat = "h:mm a<br />MMM D";
export const GraphTooltipDatetimeFormat = "%a, %b %d, %Y %I:%M:%S %P";
export const MillisecondTimeFormat = "MMM DD, YYYY HH:mm:ss.SSS z"

export function getBase64(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

/**
 *  Normalizes a data point to a target range
 * @param data - data point to be normalized
 * @param min - min value of the data
 * @param max - max value of the data
 * @param targetMin - min value of the target range
 * @param targetMax - max value of the target range
 * @returns normalized data point
 */
export function normalizeDataPoint(data: number, min: number, max: number, targetMin: number, targetMax: number) {
  return ((data - min) * (targetMax - targetMin)) / (max - min) + targetMin;
}

/**
 * Convert Array of Objects to Array of Objects grouped by key
 */
export function groupBy<T>(array: T[], property: string) {
  const hash: {
    [key: string]: T[];
  } = {};
  // split the properties into an array by '.'
  const props = property.split(".");
  // loop through the array
  for (let i = 0; i < array.length; i++) {
    // get the current item
    const item = array[i];
    // get the current property value
    const key = props.reduce(function (acc, prop) {
      // if acc is object, get the property value if not, return acc
      return acc && acc[prop];
    }, item as any);
    // if the key is not in the hash, add it
    if (!hash[key]) hash[key] = [];
    // add the current item to the hash
    hash[key].push(item);
  }
  return hash;
}

/**
 * Calc Length of each Array from Object of Arrays (e.g. {a: [1, 2, 3], b: [4, 5, 6]}) and return object of lengths
 * @param {Record<string, any[]} obj e.g. {a: [1, 2, 3], b: [4, 5, 6]}
 * @returns {{ [key : string] : number}} Object of lengths {a: 3, b: 3}
 */
export function calcLengths(obj: Record<string, any[]>): { [key: string]: number } {
  const lengths: { [key: string]: number } = {};
  for (const key in obj) {
    lengths[key] = obj[key as keyof Object].length;
  }
  return lengths;
}

export function svgJSXtoURL(jsx: JSX.Element) {
  const svgString = renderToStaticMarkup(jsx);
  const base64 = btoa(svgString);
  return `data:image/svg+xml;base64,${base64}`;
}

export function encrypt(text: string, publicKeyPem: string): string | null {
  const encryptor = new JSEncrypt();
  encryptor.setPublicKey(publicKeyPem);

  const encryptedString = encryptor.encrypt(text);
  if (!encryptedString) {
    console.error("Error encrypting string");
    return null;
  }

  return encryptedString; // Base64 encode the encrypted string
}

/**
 * Normalizes the time to the start of the resolution
 *
 * @param unixTime - The unix time to normalize
 * @param resolution - The resolution to normalize to
 * @param timezone - The timezone to normalize to
 * @returns The normalized time
 */
export function normalizeTime(unixTime: number, resolution: number, timezone: string) {
  // Convert UTC time to the specified timezone
  const momentInTimezone = moment.unix(unixTime).tz(timezone);

  // Take the max resolution of 86400 seconds (1 day) if the resolution is greater than 1 day
  const maxResolution = resolution > 86400 ? 86400 : resolution;

  // Get the timezone offset in seconds
  const offsetSeconds = momentInTimezone.utcOffset() * 60;

  // Apply offset to get local time, normalize to resolution boundary, then convert back to UTC
  const localTime = unixTime + offsetSeconds;
  const normalizedLocalTime = Math.floor(localTime / maxResolution) * maxResolution;

  return normalizedLocalTime - offsetSeconds;
}

export function handleOnlyNumber(event: React.KeyboardEvent<HTMLInputElement>) {
  if (event?.key === "-" || event?.key === "+" || event?.key === "." || event?.key.toLowerCase() === "e" ) {
    event.preventDefault();
  }
}
export function handleOnlyNumberForText(event: React.KeyboardEvent<HTMLInputElement>) {
 const regex = new RegExp("^[0-9]");
 const excludeKeys = ['Backspace','Control','Tab']
  console.log(event.key,'----key')
  if(!event.ctrlKey){
    if(event.key && !excludeKeys.includes(event.key)){
      const symbol = event.key;
      if (event?.key === "-" || event?.key === "+" || event?.key === "." || event?.key.toLowerCase() === "e" || !regex.test(symbol)) {
        event.preventDefault();
      }
    }
  }
}

export const getQueryObj = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const queryObj: any = {};
  urlParams.forEach((value, key) => {
    queryObj[key] = value;
  });

  return queryObj;
};

export const getStatusClassName = (val: string) => {
  return val === "Active" ? "text-green" : "text-red";
};

/**
 * Update an object with a nested key with a new value without mutating the original object
 * @param data Data to be updated
 * @param key Key Eg: "a.b.c" , "a" , "a.b"
 * @param value Value to be updated
 * @returns Updated Object
 */
export function updateObj<T>(data: T, key: string, value: string | number | boolean) {
  const newObj = structuredClone(data);
  const keys = key.split(".");
  let currentObj: any = newObj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!currentObj.hasOwnProperty(key) || typeof currentObj[key] !== "object") {
      // If the nested key doesn't exist or it's not an object, create an empty object at the key
      currentObj[key] = {};
    }
    currentObj = currentObj[key];
  }

  // Set the final value at the last key in the nested structure
  currentObj[keys[keys.length - 1]] = value;

  return newObj as T;
}

/**
 * Get the time difference between two dates in seconds
 * @param startTime Start Time in unix timestamp
 * @param endTime End Time in unix timestamp
 * @returns Time difference;
 */
export const getTimeDifference = (startTime: number, endTime = moment().unix()) => {
  const timeDiff = moment.duration(moment.unix(endTime).diff(moment.unix(startTime)));
  return timeDiff;
};

/**
 * Format a duration in seconds using compact units for inline UI display.
 * Examples: 3min, 2h 5min, 1d 2h.
 * @param diff Duration in seconds.
 * @returns Compact duration string.
 */
export function formatCompactDuration(diff: number) {
  const duration = moment.duration(diff, 'seconds');
  const years = duration.years() > 0 ? `${duration.years()}y` : '';
  const months = duration.months() > 0 ? `${duration.months()}mo` : '';
  const days = duration.days() > 0 ? `${duration.days()}d` : '';
  const hours = duration.hours() > 0 ? `${duration.hours()}h` : '';
  const minutes = duration.minutes() > 0 ? `${duration.minutes()}min` : '';
  const seconds = duration.seconds() > 0 ? `${duration.seconds()}s` : '';
  return `${years} ${months} ${days} ${hours} ${minutes} ${seconds}`.trim();
}

const networkInfo = {
  controlplane: {
    "4G": "MME",
    "5G": "AMF",
  },
  userplane: {
    "4G": "PGW",
    "5G": "UPF",
  },
} as const;

export const getNetworkPlaneInfo = (networkType: "4G" | "5G", plane: keyof typeof networkInfo) => {
  return networkInfo[plane][networkType];
};
export const capitalWithFirstLetter = (value: string) => {
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const getMetricsList = (selectedTemplate: MetricGraph | null) => {
  return selectedTemplate?.categories.flatMap(category =>
    category.graphs.flatMap(graph =>
      graph.metrics.map(metric => {
        const { name, key, unit } = metric;
        return { name, key , unit};
      })
    )
  ) || [];
};

export const getMetricsTemplateId = (selectedTemplate: MetricGraph | null) => {
  return selectedTemplate?.id || "";
};

/**
 * Formats a large number into compact notation and returns the value and notation separately.
 * @param {number} largeNum - The large number to format.
 * @param {boolean} removeSign - Whether to remove the sign of the large number or not. Default is false.
 * @returns {{ value: number, notation: string }} - An object containing the formatted value and notation.
 * @description
 *   - Uses Intl.NumberFormat to format the large number into parts.
 *   - Extracts the value and notation from the formatted parts.
 */
export function formatCompactNumberInParts(largeNum: number, removeSign = false): { value: number; notation: string } {
  const formatter = Intl.NumberFormat("en", { notation: "compact" });
  const value = removeSign ? Math.abs(largeNum) : largeNum;

  // Get the formatted parts using formatToParts
  const parts = formatter.formatToParts(value);

  // Extract the value and notation
  let formattedValue = "";
  let notation = "";

  for (const part of parts) {
    if (part.type === "integer" || part.type === "fraction" || part.type === "decimal") {
      formattedValue += part.value;
    } else if (part.type === "compact") {
      notation = part.value;
    }
  }

  return { value: +formattedValue, notation };
}

type Operator = "Verizon" | "T-Mobile" | "AT&T";

const VERIZON_COLOR = "#EEO200";
const AT_T_COLOR = "#067AB4";
const TMOBILE_COLOR = "#E20074";

// Variations for each operator name
const operatorVariations: Record<Operator, string[]> = {
  Verizon: ["verizon", "vzn"],
  "T-Mobile": ["t-mobile", "tmobile", "tmob", "tmo"],
  "AT&T": ["at&t", "att", "atandt"],
};

// Color mapping for each operator
const operatorColorMapping: Record<Operator, string> = {
  Verizon: VERIZON_COLOR,
  "T-Mobile": TMOBILE_COLOR,
  "AT&T": AT_T_COLOR,
};

/**
 * Maps an operator name variation to the corresponding color code.
 * @param input - The operator name variation to be mapped.
 * @returns The color code for the operator or null.
 */
export function mapOperatorToColor(input: string): string | null {
  const normalizedInput = input.toLowerCase().replace(/[^a-z]/g, ""); // Convert to lowercase and remove non-alphabetic characters

  for (const operator in operatorVariations) {
    const variations = operatorVariations[operator as Operator].map((variation) =>
      variation.toLowerCase().replace(/[^a-z]/g, "")
    );

    for (const variation of variations) {
      if (variation.includes(normalizedInput) || normalizedInput.includes(variation)) {
        return operatorColorMapping[operator as Operator];
      }
    }
  }

  return null;
}

/**
 * Acronyms that should stay fully uppercase when formatting role strings,
 * e.g. PARTNER_MSP_ADMIN -> "Partner MSP Admin". Add new ones here as needed.
 */
const ROLE_ACRONYMS = new Set(["MSP", "MSO", "RAN", "KPI"]);

/**
 *
 * @param role - The role string e.g. READONLY_TENANT or PARTNER_MSP_ADMIN
 * @returns The formatted role string e.g. Readonly Tenant or Partner MSP Admin
 */
export function roleStringFormatter(role: string) {
  return role
    .split("_")
    .filter(Boolean)
    .map(word => {
      if (ROLE_ACRONYMS.has(word.toUpperCase())) return word.toUpperCase();
      return word[0].toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

/**
 * MCS-3799: Role label shown on VMC. Prefers `displayRole` (set by the backend for partner users
 * whose effective `roles` was rewritten to RAN_KPI_ONLY); falls back to `roles`. Output is Title
 * Case to match the ProfileCard dropdown's `textTransform: capitalize`.
 */
export function getProvisionedRole(user: { roles?: string[]; displayRole?: string[] } | null | undefined): string {
  if (!user) return "";
  const source = user.displayRole?.length ? user.displayRole : (user.roles ?? []);
  return source
    .map((role) =>
      role
        .replaceAll("_", " ")
        .toLowerCase()
        .split(" ")
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
        .join(" "),
    )
    .join(", ");
}

/**
 * Merges objects in the provided array based on their endTime property.
 *
 * @param data - The array of objects to be merged.
 * @returns An array of merged Event summary objects.
 */
export function mergeObjectsByEndTime(data: EventSummary[]): EventSummary[] {
  const mergedData: any = {};

  data.forEach((obj: any) => {
    const { endTime, category, objectType, startTime, count } = obj;

    if (count === undefined) {
      return; // Skip objects that don't have a count field
    }

    if (mergedData[endTime]) {
      mergedData[endTime].count += count;
    } else {
      mergedData[endTime] = {
        category,
        endTime,
        objectType,
        startTime,
        count
      };
    }
  });

  return Object.values(mergedData);
}

/**
 * Returns the number of digits after the decimal point in a given number.
 *
 * @param {number} number - The number to check.
 * @returns {number} The number of digits after the decimal point, or 0 if the number is null or undefined.
 */
export const getNumberOfDigitsAfterDecimal = (number: number): number => {
  if (!number) return 0;

  // Convert the number to a string and find the index of the decimal point.
  const strValue = number.toString();
  const index = strValue.indexOf(".");

  // If the number is an integer (i.e., does not have a decimal point), return 0.
  if (index === -1) {
    return 0;
  }

  // Calculate and return the number of digits after the decimal point.
  return strValue.length - index - 1;
};

export const datetimeRanges: PresetsType[] = [
  { label: "Last 3 hours", value: '3hour' },
  { label: "Last 6 hours", value: '6hour' },
  { label: "Last 12 hours", value: '12hour' },
  { label: "Last 1 day", value: '1day' },
  { label: "Last 1 week", value: '1week' },
  { label: "Last 1 month", value: '1month' },
  { label: "Last 3 months", value: "3month" },
  { label: "Custom", value: 'custom' },
]

export const aggregationPeriodOptions: {label: string, value: number}[] = [
  { label: "15 Minutes", value: 900 },
  { label: "1 Hour", value: 3600 },
  { label: "1 Day", value: 86400 },
  { label: "1 Week", value: 604800 }
]

// helper to get dimensions of an image
export const imageDimensions = (file: File) =>
  new Promise<{
    width: number;
    height: number;
  }>((resolve, reject) => {
    const img = new Image();

    // the following handler will fire after a successful loading of the image
    img.onload = () => {
      const { naturalWidth: width, naturalHeight: height } = img;
      resolve({ width, height });
    };

    // and this handler will fire if there was an error with the image (like if it's not really an image or a corrupted one)
    img.onerror = () => {
      reject("There was some problem with the image.");
    };

    img.src = URL.createObjectURL(file);
  });

// transform the coordinates to another coordinate system
export const transformCoordinates = (coordinates: Array<Array<number>>, currDim: number[], newDim: number[]) => {
  return coordinates.map((coordinate) => {
    return coordinate.map((value, index) => {
      return value * (newDim[index] / currDim[index]);
    });
  });
};
export function decodeJWTToken(token: string) {
  try {
    if (!token || token === "0") return null;
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replaceAll("-", "+").replaceAll("_", "/");
    return JSON.parse(window.atob(base64));
  } catch {
    return null;
  }
}

export function truncateString(str: string, splitSize = 15) {
  if(str.length <= (splitSize * 2 + 5)) return str;
  if (str.length <= splitSize) return str;
  return str.slice(0, splitSize) + "..." + str.slice(-splitSize);
}

interface PlmnOption {
  id: string;
  label: string;
  value: string;
}

interface PlmnMapItem {
  edgeID: string;
  name: string;
}

type PlmnMap = Record<string, Array<PlmnMapItem> | string>;

interface CreatePlmnOptionsParams {
  plmns: string[];
  allowedEdgeIds: string[];
  plmnMap: PlmnMap;
  network?: {
    mcc?: string;
    mnc?: string;
    fullNetworkName?: string;
  };
  includeAllPlmns?: boolean;
  includeEdgeId?: boolean;
}

/**
 * Creates PLMN options based on the provided parameters
 * @param params - Parameters for creating PLMN options
 * @param params.plmns - Array of PLMNs to create options for
 * @param params.allowedEdgeIds - Array of edge IDs that are allowed for filtering PLMN options
 * @param params.plmnMap - Map of PLMNs to either edge information, network name or empty string
 * @param params.network - Network information for handling private PLMN cases
 * @param params.includeAllPlmns - Whether to include an "All PLMNs" option when there are multiple PLMNs
 * @param params.includeEdgeId - Whether to include edge ID in the PLMN value
 * @returns Array of PLMN options with id, label, and value
 */
export function createPlmnOptions({
  plmns,
  allowedEdgeIds,
  plmnMap,
  network,
  includeAllPlmns = false,
  includeEdgeId = false
}: CreatePlmnOptionsParams): PlmnOption[] {
  if (!plmns.length) return [];

  const plmnOptions = plmns.flatMap((plmn) => {
    const plmnData = plmnMap[plmn];
    if (Array.isArray(plmnData)) {
      // Filter out the plmn - operator name combination based on the allowed edge IDs
      // If allowedEdgeIds is empty OR item has empty edgeID (global operator), include the operator
      const filteredOptions = plmnData
        .filter((item: PlmnMapItem) => {
          const shouldInclude = item.edgeID === '' || allowedEdgeIds.includes(item.edgeID);
          return shouldInclude;
        })
        .map(({ name, edgeID }: PlmnMapItem) => ({
          id: `${plmn}-${name}`,
          label: `${plmn} ${name ? ` - ${name}` : ""}`,
          value: includeEdgeId ? `${plmn}:${edgeID}` : plmn
        }));

      if (plmn !== "315010" || filteredOptions.length !== 0) {
        return filteredOptions;
      } else {
        // This is a special case when private plmn is also assigned to an operator
        const networkPLMN = network?.mcc && network?.mnc ? `${network.mcc}${network.mnc}` : "";
        const plmnName = networkPLMN === plmn && network?.fullNetworkName
          ? network.fullNetworkName
          : "Private Cellular";
        return {
          id: `${plmn}-${plmnName}`,
          label: `${plmn} - ${plmnName}`,
          value: plmn
        };
      }
    }
    return {
      id: `${plmn}-${plmnMap[plmn]}`,
      label: `${plmn} ${plmnMap[plmn] ? ` - ${plmnMap[plmn]}` : plmn === "315010" ? ` - Private Cellular` : ""}`,
      value: plmn
    };
  });

  // add all PLMNs to the list if there are more than one PLMN and includeAllPlmns is true
  if (includeAllPlmns && plmns.length > 1) {
    const allPlmnsOption = {
      id: "all-plmns",
      label: "All PLMNs",
      value: plmns.join('|')
    };
    plmnOptions.push(allPlmnsOption);
  }

  return plmnOptions;
}

/**
 * Converts meters to feet
 * @param meters - The value in meters to convert
 * @returns The equivalent value in feet
 */
export function metersToFeet(meters: number): number {
  return meters * 3.28084;
}